import { graphHooks } from '@tunasong/graph-lib/react'
import { logger } from '@tunasong/models'
import type { Entity, EntityCommandSpec, EntityEvent, Persisted } from '@tunasong/schemas'
import { useMemo } from 'react'
import invariant from 'tiny-invariant'
import { agentDefinitionToCommands } from '../agent-definition-to-command.js'
import { aiPromptToCommand } from '../ai-prompt-to-command.js'
import { useGlobalAppContext } from '../app-context.js'
import { useCustomCommands } from './custom-commands.hook.js'
import { useAgentDefinitions } from './agent-definitions.hook.js'
import { usePlugin } from './editor-plugin.js'
import { usePlugins } from './editor-plugins.js'

export const useEntityCommands = (entity?: Persisted<Entity>) => {
  const plugin = usePlugin(entity)
  // Fall back to searching all plugins by node.type when key-based lookup fails
  const allPlugins = usePlugins('all')
  const resolvedPlugin = plugin ?? allPlugins?.find(p => p.node?.type === entity?.type)
  const globalContext = useGlobalAppContext()

  const entityCommands = resolvedPlugin?.getEntityCommands
    ? resolvedPlugin.getEntityCommands({ entity, globalContext })
    : []

  // Context commands are collected from ALL plugins — commands a plugin
  // contributes for entity types it doesn't own (e.g., playlist's "Create Playlist" for folders).
  const contextCommands = (allPlugins ?? []).flatMap(p =>
    p.getContextCommands ? p.getContextCommands({ entity, globalContext }) : []
  )

  const { executeAIPrompt, dispatch, getState } = globalContext
  const aiPromptCommands =
    resolvedPlugin?.getAIPrompts && entity && executeAIPrompt
      ? resolvedPlugin
          .getAIPrompts({ entity, globalContext })
          .map(spec => aiPromptToCommand(spec, { entity, globalContext }, executeAIPrompt))
      : []

  // Agent definitions from ALL plugins — one "Add Agent: X" command per definition
  // that matches the current entity type.
  const agentDefinitions = useAgentDefinitions(entity?.type)
  const agentDefinitionCommands =
    entity && agentDefinitions.length > 0
      ? agentDefinitions.flatMap(def =>
          agentDefinitionToCommands(def, entity, {
            dispatch,
            getState,
            navigateToEntity: globalContext.navigateToEntity,
          })
        )
      : []

  return [...entityCommands, ...contextCommands, ...aiPromptCommands, ...agentDefinitionCommands]
}

export const useAllEntityCommands = (entity?: Persisted<Entity>) => {
  const customCommands = useCustomCommands(entity)
  const entityCommands = useEntityCommands(entity)
  const commands = [...customCommands, ...entityCommands]
  return commands
}

export const useEntityCommandsForEvent = (targetEntity?: Persisted<Entity>, event?: EntityEvent) => {
  const plugins = usePlugins('all')

  const globalContext = useGlobalAppContext()

  const eventCommands =
    event && targetEntity?.eventCommands ? (targetEntity.eventCommands[event] ?? []) : ([] as EntityCommandSpec[])

  const commandEntityIds = eventCommands.map(cmd => cmd.entityId)
  const { entities } = graphHooks.useEntitiesById(commandEntityIds)

  const commands = eventCommands
    .map(cmd => {
      const cmdEntity = entities?.find(e => e.id === cmd.entityId)
      const plugin = plugins?.find(p => p.node?.type === cmdEntity?.type)
      const pluginCommands = plugin?.getEntityCommands
        ? (plugin.getEntityCommands({ entity: cmdEntity, targetEntity, globalContext }) ?? [])
        : []
      const cmds = pluginCommands?.find(c => c.id === cmd.commandId)
      return cmds
    })
    .filter(Boolean)

  // We want to return commands when all eventCommands have been resolved.
  return commands.length === eventCommands.length ? commands : []
}

export const useEntityCommandFromSpec = (cmd?: EntityCommandSpec) => {
  const { entity } = graphHooks.useEntity(cmd?.entityId)
  const commands = useAllEntityCommands(entity)
  return commands.find(c => c.id === cmd?.commandId)
}

/** Get all the executable commands from the EntityCommandSpec list */
export const useEntityCommandsFromSpecs = (cmds?: EntityCommandSpec[]) => {
  const plugins = usePlugins('all')
  const globalContext = useGlobalAppContext()
  const entityIds = cmds?.map(c => c.entityId)
  const { entities } = graphHooks.useEntitiesById(entityIds)

  const commands = useMemo(() => {
    if (!cmds || !entities) {
      return []
    }
    return entities
      .flatMap(entity => {
        const plugin = plugins?.find(p => p.node?.type === entity.type)
        if (!plugin) {
          logger.error(`No plugin found for entity type: ${entity.type}, cannot execute commands.`)
          return null
        }
        if (!plugin.getEntityCommands) {
          logger.error(`No entity commands found for entity type: ${entity.type}, cannot execute commands.`)
          return null
        }
        const entityCommandSpecs = cmds.filter(c => c.entityId === entity.id)

        const entityCommands = plugin.getEntityCommands({ entity, globalContext })

        // we inject the parameters to the commands from the entityCommandSpecs
        const commandsWithParameters = entityCommandSpecs.map(cmdSpec => {
          const command = entityCommands.find(c => c.id === cmdSpec.commandId)
          invariant(command, `Command not found for entity ${entity.id} with commandId ${cmdSpec.commandId}`)
          const wrappedFn = command.cmd ? () => command.cmd?.(cmdSpec.commandParams) : null
          logger.debug(`Command ${command.id} with parameters  is being prepared.`, {
            commandParams: cmdSpec.commandParams,
          })
          return { ...command, cmd: wrappedFn }
        })

        return commandsWithParameters
      })
      .filter(Boolean)
  }, [cmds, entities, globalContext, plugins])

  return commands
}
